'use client';

import { FC, useState, useEffect } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { registerContact } from '@/actions/hubspot/registerContact';
import { processCalculatorPdf } from '@/actions/calculator/processCalculatorPdf';
import cookies from 'js-cookie';

// HubSpot form configuration
const HUBSPOT_PORTAL_ID = '7119515';
const HUBSPOT_FORM_ID = 'f27d60b7-aa5b-464f-92b9-9ca51d1890d9';

// Field label to HubSpot internal name mapping
const FIELD_MAPPINGS: Record<string, string> = {
  'first name': 'firstname',
  firstname: 'firstname',
  'last name': 'lastname',
  lastname: 'lastname',
  email: 'email',
  'email address': 'email',
  'full name': 'fullname', // Will be split into firstname + lastname
  company: 'company',
};

// Validate full name: requires at least "X Y" where Y has 3+ characters
const validateFullName = (
  name: string,
): { isValid: boolean; error?: string } => {
  const trimmed = name.trim();
  const parts = trimmed.split(/\s+/);

  if (parts.length < 2) {
    return { isValid: false, error: 'Please enter your first and last name' };
  }

  const lastName = parts.slice(1).join(' ');
  if (lastName.length < 3) {
    return { isValid: false, error: 'Last name must be at least 3 characters' };
  }

  return { isValid: true };
};

// Split full name into first and last name
const splitFullName = (
  fullName: string,
): { firstName: string; lastName: string } => {
  const parts = fullName.trim().split(/\s+/);
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';
  return { firstName, lastName };
};

// Checkmark icon for feature list
const CheckIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <circle cx="12" cy="12" r="10" fill="#05F273" />
    <path
      d="M8 12L11 15L16 9"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Props for `LeadMagnetHero`.
 */
export type LeadMagnetHeroProps =
  SliceComponentProps<Content.LeadMagnetHeroSlice>;

/**
 * Component for "LeadMagnetHero" Slices.
 */
// ── EbookSideImageWithStats variation ───────────────────────────────────────

const EbookSideImageWithStats: FC<{
  slice: Content.LeadMagnetHeroSliceEbookSideImageWithStats;
}> = ({ slice }) => {
  const {
    logo,
    title,
    subtitle,
    ebook_image,
    tablet_mockup,
    stats,
    description,
    highlight_text,
    cta_label,
    cta_button,
    cta_background,
    background_pattern,
  } = slice.primary;

  return (
    <section
      data-slice-type="lead_magnet_hero"
      data-slice-variation="ebookSideImageWithStats"
      className="relative overflow-hidden text-white"
    >
      {/* Optional background pattern */}
      {isFilled.image(background_pattern) && (
        <PrismicNextImage
          field={background_pattern}
          fill
          className="pointer-events-none absolute inset-0 object-cover opacity-60"
          fallbackAlt=""
        />
      )}

      <div className="relative z-10 mx-auto flex max-w-[1100px] flex-col items-center gap-10 px-6 pt-12 pb-20 md:px-10 md:pt-16 md:pb-24">
        {/* Logo */}
        {isFilled.image(logo) && (
          <PrismicNextImage
            field={logo}
            className="h-auto w-[120px] object-contain"
            fallbackAlt=""
          />
        )}

        {/* Title — RichText with green-highlighted strong */}
        {isFilled.richText(title) && (
          <PrismicRichText
            field={title}
            components={{
              heading1: ({ children }) => (
                <h1 className="text-center text-[40px] font-bold leading-[1.1] md:text-[60px]">
                  {children}
                </h1>
              ),
              strong: ({ children }) => {
                // Recursively extract the plain text from the children tree, then
                // force a hard line break before " On " (e.g. "On The P&L").
                const flatten = (node: unknown): string => {
                  if (node == null || node === false) {
                    return '';
                  }
                  if (typeof node === 'string') {
                    return node;
                  }
                  if (typeof node === 'number') {
                    return String(node);
                  }
                  if (Array.isArray(node)) {
                    return node.map(flatten).join('');
                  }
                  if (typeof node === 'object' && 'props' in node) {
                    const props = (node as { props?: { children?: unknown } })
                      .props;
                    return flatten(props?.children);
                  }
                  return '';
                };
                const text = flatten(children);
                const idx = text.lastIndexOf(' On ');
                if (idx > 0) {
                  return (
                    <strong className="block font-bold text-[#00f557]">
                      {text.substring(0, idx)}
                      <br />
                      {text.substring(idx + 1)}
                    </strong>
                  );
                }
                return (
                  <strong className="block font-bold text-[#00f557]">
                    {children}
                  </strong>
                );
              },
            }}
          />
        )}

        {/* Subtitle */}
        {subtitle && (
          <p className="max-w-[700px] text-center text-[15px] font-normal leading-[1.5] text-white/80 md:text-[16px]">
            {subtitle}
          </p>
        )}

        {/* Two-column: ebook image left, stats list right */}
        <div className="mt-4 grid w-full grid-cols-1 items-center gap-8 md:grid-cols-2 md:gap-16">
          {/* Left — Tablet mockup with ebook cover overlay */}
          <div className="flex justify-center md:justify-end">
            <div className="relative w-full max-w-[560px]">
              {isFilled.image(tablet_mockup) && (
                <PrismicNextImage
                  field={tablet_mockup}
                  className="block h-auto w-full"
                  fallbackAlt=""
                />
              )}
              {isFilled.image(ebook_image) && (
                <PrismicNextImage
                  field={ebook_image}
                  className={
                    isFilled.image(tablet_mockup)
                      ? 'absolute top-[14%] left-[35%] w-[31%] object-cover shadow-[0_8px_24px_rgba(0,0,0,0.4)]'
                      : 'block h-auto w-full max-w-[460px] object-contain'
                  }
                  fallbackAlt=""
                />
              )}
            </div>
          </div>

          {/* Right — Stats list */}
          {stats && stats.length > 0 && (
            <div className="flex flex-col">
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className="border-t border-white/25 py-8 last:border-b"
                >
                  <p className="text-[18px] leading-[1.5] text-white md:text-[20px]">
                    <span className="text-[22px] font-bold text-[#00f557] md:text-[26px]">
                      {stat.source}
                    </span>{' '}
                    <span className="text-[22px] text-[#00f557] md:text-[26px]">
                      →
                    </span>{' '}
                    <PrismicRichText
                      field={stat.description}
                      components={{
                        paragraph: ({ children }) => (
                          <span className="text-white/90">{children}</span>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-bold text-[#00f557]">
                            {children}
                          </strong>
                        ),
                      }}
                    />
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Description paragraph */}
        {isFilled.richText(description) && (
          <div className="max-w-[760px] text-center text-[15px] leading-[1.55] text-white/80 md:text-[16px]">
            <PrismicRichText field={description} />
          </div>
        )}

        {/* Highlight (green) line */}
        {highlight_text && (
          <p className="max-w-[760px] text-center text-[15px] font-semibold leading-[1.5] text-[#00f557] md:text-[16px]">
            {highlight_text}
          </p>
        )}

        {/* CTA bar */}
        {(cta_label || isFilled.link(cta_button)) && (
          <div className="relative mt-4 flex w-full max-w-[760px] flex-col items-center justify-between gap-4 overflow-hidden rounded-2xl border border-[#00f557]/40 bg-[#031a14] px-6 py-4 sm:flex-row sm:gap-6">
            {isFilled.image(cta_background) && (
              <PrismicNextImage
                field={cta_background}
                fill
                className="pointer-events-none absolute inset-0 -z-0 object-cover opacity-60"
                fallbackAlt=""
              />
            )}
            {cta_label && (
              <p className="relative z-10 flex-1 text-center text-[16px] font-medium text-white sm:text-left md:text-[18px]">
                {cta_label}
              </p>
            )}
            {isFilled.link(cta_button) &&
              (() => {
                const ctaUrl =
                  'url' in cta_button ? (cta_button.url ?? '') : '';
                // Prismic's Link to Media field exposes the original upload
                // filename via `.name`. Pass it through so the proxy saves the
                // file with that name instead of Prismic CDN's hashed slug.
                const ctaName =
                  'name' in cta_button &&
                  typeof cta_button.name === 'string' &&
                  cta_button.name
                    ? cta_button.name
                    : '';
                // Downloadable files (PDFs, archives, office docs) are routed
                // through /api/download so the browser receives a same-origin
                // response with Content-Disposition: attachment — forcing a
                // save even when the actual file lives on Prismic's CDN.
                const isDownloadable = /\.(pdf|zip|docx?|xlsx?)$/i.test(ctaUrl);
                const href = isDownloadable
                  ? `/api/download?url=${encodeURIComponent(ctaUrl)}${
                      ctaName ? `&name=${encodeURIComponent(ctaName)}` : ''
                    }`
                  : ctaUrl;
                return (
                  <a
                    href={href}
                    className="relative z-10 inline-flex shrink-0 items-center justify-center rounded-full bg-[#00f557] px-6 py-2.5 text-[14px] font-bold text-[#002726] transition-opacity hover:opacity-90 md:text-[15px]"
                  >
                    {cta_button.text || 'Read it now'}
                  </a>
                );
              })()}
          </div>
        )}
      </div>
    </section>
  );
};

// ── Default variation (with feature list, form & links) ────────────────────

const DefaultWithFeatureListFormLinks: FC<{
  slice: Content.LeadMagnetHeroSliceDefaultWithFeatureListFormLinks;
}> = ({ slice }) => {
  const {
    title,
    description,
    cta_links,
    features,
    form_fields,
    form_cta_text,
  } = slice.primary;

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [calculatorValues, setCalculatorValues] = useState<{
    sloc: string;
    repoSize: string;
    devCount: string;
    multiplier: string;
    inHouseCost: string;
    velxCost: string;
    timeReduction: string;
    efficiencyGain: string;
  } | null>(null);

  // Read calculator values from URL params on mount and on URL changes
  useEffect(() => {
    const readParams = () => {
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const sloc = params.get('sloc');
        const repoSize = params.get('repo_size');
        const devCount = params.get('dev_count');
        const multiplier = params.get('multiplier');
        const inHouseCost = params.get('in_house_cost');
        const velxCost = params.get('velx_cost');
        const timeReduction = params.get('time_reduction');
        const efficiencyGain = params.get('efficiency_gain');

        if (
          sloc ||
          repoSize ||
          devCount ||
          multiplier ||
          inHouseCost ||
          velxCost
        ) {
          setCalculatorValues({
            sloc: sloc || '0',
            repoSize: repoSize || '0',
            devCount: devCount || '0',
            multiplier: multiplier || '',
            inHouseCost: inHouseCost || '0',
            velxCost: velxCost || '0',
            timeReduction: timeReduction || '0',
            efficiencyGain: efficiencyGain || '0',
          });
        }
      }
    };

    // Read on mount
    readParams();

    // Also listen for URL changes (popstate)
    window.addEventListener('popstate', readParams);

    // Check periodically in case URL was updated via replaceState
    const interval = setInterval(readParams, 500);

    return () => {
      window.removeEventListener('popstate', readParams);
      clearInterval(interval);
    };
  }, []);

  const handleInputChange = (fieldLabel: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [fieldLabel]: value,
    }));
    // Clear field error when user types
    if (fieldErrors[fieldLabel]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldLabel];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);
    setFieldErrors({});

    // Validate Full Name field if present
    const newErrors: Record<string, string> = {};
    Object.entries(formData).forEach(([label, value]) => {
      const normalizedLabel = label.toLowerCase().trim();
      if (normalizedLabel === 'full name' || normalizedLabel === 'fullname') {
        const validation = validateFullName(value);
        if (!validation.isValid) {
          newErrors[label] = validation.error || 'Invalid name';
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      // Convert form field labels to HubSpot field names
      const hubspotProperties: Record<string, string> = {};

      // Map user-entered fields
      Object.entries(formData).forEach(([label, value]) => {
        const normalizedLabel = label.toLowerCase().trim();

        // Handle Full Name specially - split into firstname and lastname
        if (normalizedLabel === 'full name' || normalizedLabel === 'fullname') {
          const { firstName, lastName } = splitFullName(value);
          hubspotProperties['firstname'] = firstName;
          hubspotProperties['lastname'] = lastName;
        } else {
          // Use explicit mapping if available, otherwise convert to snake_case
          const fieldName =
            FIELD_MAPPINGS[normalizedLabel] ||
            normalizedLabel.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
          hubspotProperties[fieldName] = value;
        }
      });

      // Add calculator values if available
      if (calculatorValues) {
        hubspotProperties['velx_calculator_sloc'] = calculatorValues.sloc;
        hubspotProperties['velx_calculator_repository_size__mb_'] =
          calculatorValues.repoSize;
        hubspotProperties['velx_calculator_developer_count'] =
          calculatorValues.devCount;
        // Add multiplier checkboxes (semicolon-separated for HubSpot)
        if (calculatorValues.multiplier) {
          hubspotProperties['multiplier'] = calculatorValues.multiplier;
        }
        // Add calculated cost values
        hubspotProperties['velx_calculator_in_house_cost'] =
          calculatorValues.inHouseCost;
        hubspotProperties['velx_calculator_velx_cost'] =
          calculatorValues.velxCost;
        hubspotProperties['velx_calculator_time_reduction__'] =
          calculatorValues.timeReduction;
        hubspotProperties['velx_calculator_efficiency_gains'] =
          calculatorValues.efficiencyGain;
      }

      const { postSubmitAction } = await registerContact({
        properties: hubspotProperties,
        pageUri: typeof window !== 'undefined' ? window.location.href : '',
        pageName: typeof document !== 'undefined' ? document.title : '',
        hubspotPortalIdForm: HUBSPOT_PORTAL_ID,
        hubspotGuidForm: HUBSPOT_FORM_ID,
        hubspotutkCookie: cookies.get('hubspotutk'),
      });

      if (postSubmitAction?.type === 'redirect' && postSubmitAction.value) {
        window.location.href = postSubmitAction.value;
        return;
      }

      setIsSubmitSuccessful(true);
      setFormData({}); // Reset form

      // Fire-and-forget: Generate PDF and upload to HubSpot
      // This runs in the background and doesn't block the user
      if (calculatorValues) {
        const email = hubspotProperties['email'] || '';
        const firstName = hubspotProperties['firstname'] || '';
        const lastName = hubspotProperties['lastname'] || '';
        const company = hubspotProperties['company'] || '';

        processCalculatorPdf({
          email,
          firstName,
          lastName,
          company,
          calculatorValues: {
            sloc: calculatorValues.sloc,
            repoSize: calculatorValues.repoSize,
            devCount: calculatorValues.devCount,
            inHouseCost: calculatorValues.inHouseCost,
            velxCost: calculatorValues.velxCost,
            timeReduction: calculatorValues.timeReduction,
          },
        }).catch((err) => {
          // Silent catch - PDF generation failure should not affect user experience
          console.error('Background PDF processing failed:', err);
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setErrorMessage('Something went wrong. Please try again.');
      setIsSubmitSuccessful(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      data-slice-type="lead_magnet_hero"
      data-slice-variation={slice.variation}
      className="relative bg-[#0E1010] py-20 px-4 md:px-8 lg:px-[150px]"
    >
      <div className="max-w-[1140px] mx-auto">
        <div className="flex flex-col gap-[50px] items-center w-full">
          {/* Header Section - Title, Description, CTA Links */}
          <div className="flex flex-col gap-6 items-center justify-center w-full">
            {/* Title */}
            {isFilled.richText(title) && (
              <h1 className="text-white text-[32px] md:text-[42px] font-medium leading-[1.2] text-center w-full">
                <PrismicRichText
                  field={title}
                  components={{
                    heading1: ({ children }) => <>{children}</>,
                  }}
                />
              </h1>
            )}

            {/* Description */}
            {isFilled.richText(description) && (
              <div className="text-white text-base md:text-[18px] leading-[1.5] text-center w-full">
                <PrismicRichText field={description} />
              </div>
            )}

            {/* CTA Links */}
            {cta_links && cta_links.length > 0 && (
              <div className="flex flex-wrap gap-6 items-center justify-center">
                {cta_links.map((item, index) => {
                  const variant = item.cta_link.variant || 'primary';
                  const isPrimary = variant === 'primary';

                  return (
                    <PrismicNextLink
                      key={`cta-${index}`}
                      field={item.cta_link}
                      className={`
                        px-6 py-3 rounded-full font-bold text-base text-white
                        transition-all duration-200 hover:opacity-90
                        ${
                          isPrimary
                            ? 'bg-[rgba(234,234,234,0.3)] border border-[#eaeaea]'
                            : 'bg-transparent border border-[#eaeaea]'
                        }
                      `}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Content Section - Features & Form */}
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-[60px] items-start lg:items-center w-full">
            {/* Left - Feature Cards */}
            <div className="flex flex-col gap-6 items-start justify-center w-full lg:w-[610px] lg:flex-shrink-0">
              {features?.map((feature, index) => (
                <div
                  key={`feature-${index}`}
                  className="backdrop-blur-[10px] bg-gradient-to-b from-[rgba(15,66,58,0.4)] to-transparent p-6 rounded-2xl shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] w-full"
                >
                  <div className="flex gap-2 items-start justify-center w-full">
                    {/* Icon */}
                    <div className="w-6 h-6 flex-shrink-0">
                      {isFilled.image(feature.feature_icon) ? (
                        <PrismicNextImage
                          field={feature.feature_icon}
                          className="w-6 h-6"
                        />
                      ) : (
                        <CheckIcon />
                      )}
                    </div>
                    {/* Text */}
                    <p className="flex-1 text-white text-base md:text-[18px] leading-[1.4]">
                      {feature.feature_text}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right - Form */}
            <div className="w-full lg:flex-1 lg:min-w-0">
              {isSubmitSuccessful ? (
                /* Success State */
                <div className="backdrop-blur-[10px] bg-[#1f2228] p-6 rounded-2xl shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] flex flex-col gap-6 items-center justify-center min-h-[300px]">
                  <div className="w-16 h-16 bg-[#05F273] rounded-full flex items-center justify-center">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 12L10 17L20 7"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h3 className="text-white text-2xl font-semibold text-center">
                    Thank you!
                  </h3>
                  <p className="text-white/80 text-base text-center">
                    Your request has been submitted. We&apos;ll send your
                    detailed report shortly.
                  </p>
                </div>
              ) : (
                /* Form */
                <form
                  onSubmit={handleSubmit}
                  className="backdrop-blur-[10px] bg-[#1f2228] p-6 rounded-2xl shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] flex flex-col gap-6"
                >
                  {/* Error Message */}
                  {errorMessage && (
                    <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 text-red-400 text-sm text-center">
                      {errorMessage}
                    </div>
                  )}

                  {form_fields?.map((field, index) => {
                    const fieldLabel = field.field_label || '';
                    const hasError = !!fieldErrors[fieldLabel];

                    return (
                      <div
                        key={`field-${index}`}
                        className="flex flex-col gap-2 w-full"
                      >
                        <label className="text-white text-base leading-[1.5]">
                          {fieldLabel}
                          {field.is_required && ' *'}
                        </label>
                        <input
                          type={
                            fieldLabel.toLowerCase().includes('email')
                              ? 'email'
                              : 'text'
                          }
                          placeholder={field.field_placeholder || 'Type here'}
                          required={field.is_required || false}
                          value={formData[fieldLabel] || ''}
                          onChange={(e) =>
                            handleInputChange(fieldLabel, e.target.value)
                          }
                          className={`bg-[rgba(14,17,16,0.4)] px-3 py-2 h-12 rounded-lg text-white text-base placeholder:text-white/30 focus:outline-none focus:ring-1 ${
                            hasError
                              ? 'ring-1 ring-red-500 focus:ring-red-500'
                              : 'focus:ring-[#05F273]'
                          }`}
                        />
                        {hasError && (
                          <p className="text-red-400 text-sm">
                            {fieldErrors[fieldLabel]}
                          </p>
                        )}
                      </div>
                    );
                  })}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 rounded-full bg-[rgba(234,234,234,0.3)] border border-[#eaeaea] text-white font-bold text-base transition-all duration-200 hover:opacity-90 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : form_cta_text || 'Submit'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Main component (dispatches by variation) ────────────────────────────────

const LeadMagnetHero: FC<LeadMagnetHeroProps> = ({ slice }) => {
  if (slice.variation === 'ebookSideImageWithStats') {
    return <EbookSideImageWithStats slice={slice} />;
  }
  return <DefaultWithFeatureListFormLinks slice={slice} />;
};

export default LeadMagnetHero;
